import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { ArrowLeft } from 'lucide-react';
import { auth, db } from './firebase';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  arrayUnion,
  serverTimestamp
} from 'firebase/firestore';

const COUNTRIES = [
  { iso: 'US', name: 'United States', code: '1', flag: '🇺🇸' },
  { iso: 'GB', name: 'United Kingdom', code: '44', flag: '🇬🇧' },
];

const detectCountry = () => {
  try {
    const locale = navigator.language || 'en-US';
    const region = new Intl.Locale(locale).region;
    return COUNTRIES.find(c => c.iso === region) || COUNTRIES.find(c => c.iso === 'US');
  } catch {
    return COUNTRIES.find(c => c.iso === 'US');
  }
};

const Header = ({ onLogoClick }) => (
  <header style={styles.header}>
    <div style={styles.headerContent}>
      <div style={styles.logoContainer} onClick={onLogoClick}>
        <img
          src="https://firebasestorage.googleapis.com/v0/b/ss-web-rate.firebasestorage.app/o/star-filled-fiveointed-shape-3.png?alt=media&token=a90a8c97-594c-49f0-82f0-a00519fbbd3a"
          alt="Star icon"
          style={styles.logo}
        />
        <h1 style={styles.logoText}>SocialStar Partners</h1>
      </div>
    </div>
  </header>
);

const RecruitSignup = () => {
  const { recruiterId } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState('phone');

  const [isLoadingRecruiter, setIsLoadingRecruiter] = useState(true);
  const [recruiterName, setRecruiterName] = useState('');
  const [recruiterExists, setRecruiterExists] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(detectCountry());
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');

  const [code, setCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [isResending, setIsResending] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const formattedPhone = '+' + selectedCountry.code + phoneNumber.replace(/\D/g, '');

  const filteredCountries = COUNTRIES.filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    c.code.includes(countrySearch) ||
    c.iso.toLowerCase().includes(countrySearch.toLowerCase())
  );

  useEffect(() => {
    const validateRecruiter = async () => {
      try {
        if (!recruiterId) {
          setRecruiterExists(false);
          setIsLoadingRecruiter(false);
          return;
        }
        const recruiterDoc = await getDoc(doc(db, 'affiliates', recruiterId));
        if (!recruiterDoc.exists()) {
          setRecruiterExists(false);
          setIsLoadingRecruiter(false);
          return;
        }
        const data = recruiterDoc.data();
        setRecruiterName(data.firstName || 'Your Friend');
        setRecruiterExists(true);
      } catch (err) {
        console.error('Error fetching recruiter:', err);
        setRecruiterExists(false);
      } finally {
        setIsLoadingRecruiter(false);
      }
    };
    validateRecruiter();
  }, [recruiterId]);

  useEffect(() => {
    if (step !== 'phone' || isLoadingRecruiter) return;
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
    }
    return () => {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    };
  }, [step, isLoadingRecruiter]);

  const handleSendCode = async () => {
    setError('');
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name');
      return;
    }
    const digits = phoneNumber.replace(/\D/g, '');
    if (!digits || digits.length < 6) {
      setError('Please enter a valid phone number');
      return;
    }
    if (!recruiterExists) {
      setError('Invalid recruiter link');
      return;
    }
    setIsLoading(true);
    try {
      const appVerifier = window.recaptchaVerifier;
      const result = await signInWithPhoneNumber(auth, formattedPhone, appVerifier);
      setConfirmationResult(result);
      setStep('verify');
    } catch (err) {
      console.error('Send code error:', err);
      setError('Failed to send code. Please check your number and try again.');
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
        window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
      }
    }
    setIsLoading(false);
  };

  const handleVerify = async () => {
    setError('');
    if (!code || code.length < 4) {
      setError('Please enter the verification code');
      return;
    }
    if (!confirmationResult) {
      setError('Session expired. Please go back and try again.');
      return;
    }
    setIsLoading(true);
    try {
      const userCredential = await confirmationResult.confirm(code);
      const newUserId = userCredential.user.uid;

      await setDoc(doc(db, 'affiliates', newUserId), {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        recruitedBy: recruiterId,
        balance: 0.0,
        totalEarnings: 0.0,
        totalWithdrawn: 0.0,
        status: 'active',
        createdAt: serverTimestamp(),
        canCreateLinks: true,
      });

      await setDoc(doc(db, 'affiliates', recruiterId, 'recruits', newUserId), {
        recruitId: newUserId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        joinedAt: serverTimestamp(),
        recruiterEarnings: 0.0,
        totalRatings: 0
      });

      await updateDoc(doc(db, 'affiliates', recruiterId), {
        totalRecruits: arrayUnion(newUserId)
      });

      setStep('success');
    } catch (err) {
      console.error('Verification error:', err);
      if (err.code === 'auth/invalid-verification-code') {
        setError('Invalid code. Please check and try again.');
      } else if (err.code === 'auth/code-expired') {
        setError('Code expired. Please go back and request a new one.');
      } else {
        setError('Verification failed. Please try again.');
      }
    }
    setIsLoading(false);
  };

  const handleResend = async () => {
    setError('');
    setIsResending(true);
    try {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
      const result = await signInWithPhoneNumber(auth, formattedPhone, window.recaptchaVerifier);
      setConfirmationResult(result);
    } catch (err) {
      console.error('Resend error:', err);
      setError('Failed to resend code. Please try again.');
    }
    setIsResending(false);
  };

  if (isLoadingRecruiter) {
    return (
      <div style={styles.container}>
        <Header onLogoClick={() => navigate('/')} />
        <div style={styles.authContainer}>
          <div style={styles.authCard}>
            <div style={styles.spinner} />
          </div>
        </div>
        <style>{css}</style>
      </div>
    );
  }

  if (!recruiterExists) {
    return (
      <div style={styles.container}>
        <Header onLogoClick={() => navigate('/')} />
        <div style={styles.authContainer}>
          <div style={styles.authCard}>
            <h2 style={styles.title}>Invalid Link</h2>
            <p style={styles.errorText}>This recruiter link is invalid or has expired.</p>
            <button onClick={() => navigate('/')} style={styles.primaryButton}>
              Go to Homepage
            </button>
          </div>
        </div>
        <style>{css}</style>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div style={styles.container}>
        <Header onLogoClick={() => navigate('/')} />
        <div style={styles.authContainer}>
          <div style={styles.authCard}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '52px', marginBottom: '24px', lineHeight: 1 }}>
                🎉
              </div>
              <h2 style={styles.title}>Account Created</h2>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '15px', margin: '0 0 36px' }}>
                Download our partner app and sign in to get started
              </p>
              <button
                onClick={() => window.open('https://apps.apple.com/us/app/socialstar-partners/id6751140592', '_blank')}
                style={styles.primaryButton}
              >
                Continue in App
              </button>
            </div>
          </div>
        </div>
        <style>{css}</style>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div style={styles.container}>
        <Header onLogoClick={() => navigate('/')} />
        <div id="recaptcha-container" />
        <div style={styles.authContainer}>
          <div style={styles.authCard}>
          <button
            onClick={() => { setStep('phone'); setCode(''); setError(''); }}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              padding: 0,
              marginBottom: '20px'
            }}
          >
            <ArrowLeft size={27} color="white" strokeWidth={2} />
          </button>

            <h2 style={styles.title}>Enter Your Code</h2>
            <p style={{
              color: 'rgba(255,255,255,0.55)',
              fontSize: '15px',
              textAlign: 'center',
              margin: '0 0 28px'
            }}>
              We sent a 6-digit code to {formattedPhone}
            </p>

            <input
              type="number"
              inputMode="numeric"
              placeholder="6-digit code"
              value={code}
              onChange={e => setCode(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleVerify(); }}
              style={styles.input}
            />

            {error && <p style={styles.errorText}>{error}</p>}

            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
                <div style={styles.spinner} />
              </div>
            ) : (
              <div>
                <button onClick={handleVerify} style={styles.primaryButton}>
                  Continue
                </button>
                <button
                  onClick={handleResend}
                  disabled={isResending}
                  style={{
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    cursor: isResending ? 'not-allowed' : 'pointer',
                    marginTop: '20px',
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '14px'
                  }}
                >
                  {isResending ? 'Sending...' : 'Resend Code'}
                </button>
              </div>
            )}
          </div>
        </div>
        <style>{css}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Header onLogoClick={() => navigate('/')} />
      <div id="recaptcha-container" />
      <div style={styles.authContainer}>
        <div style={styles.authCard}>
          <div style={styles.headerSection}>
            <h2 style={styles.title}>Get Started</h2>
            <p style={styles.recruiterNote}>
              You're joining with <strong>{recruiterName}'s</strong> invite
            </p>
          </div>

          <div style={styles.form}>
            <div style={styles.nameRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  placeholder="First name"
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  placeholder="Last name"
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Phone Number</label>
              <div style={{ display: 'flex', height: '56px' }}>
                <button
                  onClick={() => setShowCountryPicker(true)}
                  style={{
                    backgroundColor: '#323862',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px 0 0 8px',
                    padding: '0 14px',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontFamily: 'inherit'
                  }}
                >
                  {selectedCountry.flag} +{selectedCountry.code}
                </button>
                <input
                  type="tel"
                  placeholder="Enter phone number"
                  value={phoneNumber}
                  onChange={e => setPhoneNumber(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSendCode(); }}
                  style={{
                    width: '100%',
                    padding: '16px',
                    backgroundColor: '#323862',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderLeft: 'none',
                    borderRadius: '0 8px 8px 0',
                    color: 'white',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {error && <p style={styles.errorText}>{error}</p>}

            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0' }}>
                <div style={styles.spinner} />
              </div>
            ) : (
              <button onClick={handleSendCode} style={styles.primaryButton}>
                Continue
              </button>
            )}
          </div>
        </div>
      </div>

      {showCountryPicker && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'flex-end'
          }}
          onClick={() => setShowCountryPicker(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxHeight: '70vh',
              backgroundColor: '#1A2245',
              borderRadius: '20px 20px 0 0',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <div style={{ padding: '16px 16px 8px', flexShrink: 0 }}>
              <input
                autoFocus
                placeholder="Search country..."
                value={countrySearch}
                onChange={e => setCountrySearch(e.target.value)}
                style={{
                  width: '100%', height: 44,
                  backgroundColor: '#3B4374',
                  border: 'none', borderRadius: 10,
                  padding: '0 16px', color: 'white',
                  fontSize: 16, boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {filteredCountries.map((country, i) => (
                <div key={country.iso}>
                  <button
                    onClick={() => {
                      setSelectedCountry(country);
                      setShowCountryPicker(false);
                      setCountrySearch('');
                    }}
                    style={{
                      width: '100%', padding: '14px 20px',
                      backgroundColor: selectedCountry.iso === country.iso ? '#2A3255' : 'transparent',
                      border: 'none', cursor: 'pointer',
                      display: 'flex', alignItems: 'center',
                      gap: 12, textAlign: 'left', fontFamily: 'inherit'
                    }}
                  >
                    <span style={{ fontSize: 22 }}>{country.flag}</span>
                    <span style={{ flex: 1, color: 'white', fontSize: 16, fontWeight: 600 }}>{country.name}</span>
                    <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 15 }}>+{country.code}</span>
                  </button>
                  {i < filteredCountries.length - 1 && (
                    <div style={{ height: 1, backgroundColor: 'rgba(255,255,255,0.08)', margin: '0 20px' }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{css}</style>
    </div>
  );
};

const css = `
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
  input::placeholder { color: rgba(255,255,255,0.5); }
  input { outline: none; caret-color: white; }
  .grecaptcha-badge { visibility: hidden !important; }
`;

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#10183C',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    backgroundColor: '#1A2245'
  },
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '17px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    cursor: 'pointer'
  },
  logo: {
    width: '30px',
    height: '30px'
  },
  logoText: {
    margin: '2px 0 0 0',
    fontSize: '18px',
    color: 'white'
  },
  authContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px'
  },
  authCard: {
    width: '100%',
    maxWidth: '420px',
    backgroundColor: '#1A2245',
    borderRadius: '5px',
    padding: '40px'
  },
  headerSection: {
    textAlign: 'center',
    marginBottom: '40px'
  },
  title: {
    color: 'white',
    fontSize: '25px',
    fontWeight: 'bold',
    margin: '0 0 10px 0',
    textAlign: 'center'
  },
  recruiterNote: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: '16px',
    margin: 0,
    textAlign: 'center'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  nameRow: {
    display: 'flex',
    gap: '12px'
  },
  formGroup: {
    flex: 1
  },
  label: {
    display: 'block',
    color: 'white',
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '8px'
  },
  input: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#323862',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '8px',
    color: 'white',
    fontSize: '16px',
    boxSizing: 'border-box'
  },
  errorText: {
    color: '#ff6b7a',
    fontWeight: '700',
    fontSize: '14px',
    margin: '8px 0 0',
    textAlign: 'center'
  },
  primaryButton: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#4169E1',
    color: 'white',
    border: 'none',
    borderRadius: '200px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    marginTop: '8px'
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid rgba(255,255,255,0.2)',
    borderTop: '3px solid white',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  }
};

export default RecruitSignup;